import { useCallback, useMemo, useState } from 'react'
import type { TaskSignal } from '@indipia/shared'
import { useTaskSignals, useTodayTodos } from './useTaskSignals'
import { hasRedYellow } from '../constants/signalView'

/**
 * 간트·캘린더가 함께 쓰는 신호 층 — 서버 신호(공종별 3줄)·오늘 할일 조회 + [신호등 켜기]·[빨강·노랑만] 토글 + 사유 팝오버·할일 패널 열림 상태.
 * 훅은 뷰 안에서 호출한다(스케줄러 페이지·스튜디오 드로어 두 마운트 모두 같은 쿼리 키를 공유).
 * 서버 신호가 비면(오류·SIGNAL_MISSING) 폴백 없이 isError 로 노출하고, 호출 화면이 명시적 오류 표시를 한다.
 */
export interface SignalPopoverState { taskId: number; anchor: { x: number; y: number } }
export interface SignalPanelState { anchor: { x: number; y: number } }

/**
 * 신호 층 초기값 — 호출 쪽이 정한다. 주지 않으면 신호등 꺼짐(딥링크·메뉴 진입의 기본).
 * 나의 현장 [진행현황] 진입(스튜디오 드로어)만 { on: true, onlyRY: true } 를 주입한다(941 S15-3).
 * 초기값은 마운트 때 한 번만 쓴다 — 딥링크 소비·현장 전환은 사용자가 정한 켜짐 상태를 바꾸지 않는다.
 */
export interface SignalLayerDefaults { on?: boolean; onlyRY?: boolean }

/**
 * @param hiddenWorkTypeCodes 신호등을 그리지 않는 공종 코드 — [빨강·노랑만] 대상 · 개수에서도 뺀다.
 *   간트는 스케줄 전용 행정 공정(공종 마스터 scheduleOnlyYn='Y', 세대동의서 청구 등)을 넘긴다.
 * @param unavailable true 면 신호등을 켤 수 없다(현장 종료 스케줄 — isSignalUnavailableSchedule). 켜짐 · 빨강·노랑만을
 *   꺼짐으로 내보내고 토글은 무시한다. 사용자가 켜 둔 값은 지우지 않아, 종료가 풀리면 그대로 돌아온다.
 */
export function useSignalLayer(siteId: number | null | undefined, defaults?: SignalLayerDefaults, hiddenWorkTypeCodes?: ReadonlySet<string>,
                               unavailable = false) {
  const [signalOnState, setSignalOn] = useState(defaults?.on === true)
  // [빨강·노랑만] 은 신호등이 켜진 때만 의미가 있어 꺼짐 초기값이면 함께 꺼둔다
  const [onlyRYState, setOnlyRY] = useState(defaults?.on === true && defaults?.onlyRY === true)
  const signalOn = !unavailable && signalOnState
  const onlyRY = !unavailable && onlyRYState
  const [popover, setPopover] = useState<SignalPopoverState | null>(null)
  const [panel, setPanel] = useState<SignalPanelState | null>(null)

  const signalsQ = useTaskSignals(siteId)
  const todosQ = useTodayTodos(siteId, { enabled: siteId != null })

  const byTask = useMemo(() => {
    const m = new Map<number, TaskSignal>()
    for (const t of signalsQ.data ?? []) m.set(t.taskId, t)
    return m
  }, [signalsQ.data])

  /** 빨강·노랑 신호가 있는 공종 id — [빨강·노랑만 N] 의 N 은 이 집합의 크기 */
  const ryTaskIds = useMemo(() => {
    const s = new Set<number>()
    for (const t of signalsQ.data ?? []) {
      if (hiddenWorkTypeCodes?.has(t.workTypeCode)) continue
      if (hasRedYellow(t)) s.add(t.taskId)
    }
    return s
  }, [signalsQ.data, hiddenWorkTypeCodes])

  const todoCount = todosQ.data ? todosQ.data.summary.pendingCount + todosQ.data.summary.todayCount : 0

  const toggleSignal = useCallback(() => {
    if (unavailable) return
    setSignalOn(v => {
      if (v) { setOnlyRY(false); setPopover(null) }
      return !v
    })
  }, [unavailable])
  const closePopover = useCallback(() => setPopover(null), [])
  const closePanel = useCallback(() => setPanel(null), [])

  return {
    signalOn, toggleSignal, unavailable,
    onlyRY, setOnlyRY,
    signalsQ, byTask, ryTaskIds,
    todosQ, todoCount,
    popover, setPopover, closePopover,
    panel, setPanel, closePanel,
  }
}

export type SignalLayer = ReturnType<typeof useSignalLayer>
